import {DateTime} from 'luxon'
import type {ServiceReportItemResponse} from '@common/models'
import {parseSlashDateToDateTime} from '@common/utils/dates'

export type ServiceStatus = 'onsite' | 'scheduled' | 'complete'

export const TEXT_SAFETY_GAP_DAYS = 12
export const STACK_OFFSET_PX = 24

export type ServiceReportPlacement = {
  serviceId: string
  name: string
  staff: string[]
  startDate: string
  endDate: string
  startIdx: number
  endIdx: number
  track: number
  stackLevel: number
  status: ServiceStatus
}

type DateRange = {
  startIdx: number
  endIdx: number
}

type PlacementItem = Omit<ServiceReportPlacement, 'track' | 'stackLevel'> & {
  displayRange: DateRange
}

type TrackService = PlacementItem & {
  stackLevel: number
}

type TrackState = {
  services: TrackService[]
}

const resolveStatus = (
  today: DateTime,
  start: DateTime,
  end: DateTime,
): ServiceStatus => {
  if (today < start) return 'scheduled'
  if (today > end) return 'complete'
  return 'onsite'
}

const resolveDisplayEndIdx = (
  startIdx: number,
  actualEndIdx: number,
  totalDays: number,
): number => {
  const actualDurationDays = Math.max(1, actualEndIdx - startIdx + 1)
  const minDisplayDays = Math.min(
    4,
    Math.max(2, Math.ceil(actualDurationDays * 1.5)),
  )
  const minEndIdx = Math.min(totalDays, startIdx + minDisplayDays - 1)
  return Math.max(actualEndIdx, minEndIdx)
}

export const rangesOverlap = (a: DateRange, b: DateRange): boolean =>
  a.startIdx <= b.endIdx && b.startIdx <= a.endIdx

export const canShareTrack = (
  trackServices: {displayRange: DateRange}[],
  candidate: DateRange,
): boolean =>
  trackServices.every((service) => {
    const existing = service.displayRange
    if (candidate.startIdx > existing.endIdx) {
      return true
    }
    return candidate.startIdx - existing.startIdx >= TEXT_SAFETY_GAP_DAYS
  })

export const resolveStackLevel = (
  trackServices: {displayRange: DateRange; stackLevel: number}[],
  candidate: DateRange,
): number => {
  let level = 0
  while (
    trackServices.some(
      (service) =>
        service.stackLevel === level &&
        rangesOverlap(service.displayRange, candidate),
    )
  ) {
    level += 1
  }
  return level
}

export const buildServicesReportPlacements = (
  services: ServiceReportItemResponse[],
  windowStart: DateTime,
  totalDays: number,
  today: DateTime = DateTime.now().startOf('day'),
): ServiceReportPlacement[] => {
  const items = services
    .map((service) => {
      const startDateTime = parseSlashDateToDateTime(service.startDate)
      const endDateTime = parseSlashDateToDateTime(service.endDate)
      if (!startDateTime || !endDateTime) return null

      const startIdx =
        Math.max(0, Math.floor(startDateTime.diff(windowStart, 'days').days)) +
        1
      const actualEndIdx = Math.min(
        totalDays,
        Math.floor(endDateTime.diff(windowStart, 'days').days) + 1,
      )
      const displayEndIdx = resolveDisplayEndIdx(
        startIdx,
        actualEndIdx,
        totalDays,
      )
      const displayRange = {
        startIdx,
        endIdx: Math.max(startIdx, displayEndIdx),
      }

      return {
        serviceId: service.serviceId,
        name: service.serviceName,
        staff: service.staff || [],
        startDate: service.startDate || '',
        endDate: service.endDate || '',
        startIdx: displayRange.startIdx,
        endIdx: displayRange.endIdx,
        displayRange,
        status: resolveStatus(today, startDateTime, endDateTime),
      }
    })
    .filter(Boolean) as (Omit<PlacementItem, 'track' | 'stackLevel'> & {
    displayRange: DateRange
  })[]

  items.sort(
    (a, b) =>
      a.startIdx - b.startIdx ||
      a.endIdx - b.endIdx ||
      a.name.localeCompare(b.name),
  )

  const tracks: TrackState[] = []
  const placements: ServiceReportPlacement[] = []

  items.forEach((item) => {
    const candidateRange = item.displayRange
    let track = tracks.find((candidateTrack) =>
      canShareTrack(candidateTrack.services, candidateRange),
    )

    if (!track) {
      track = {services: []}
      tracks.push(track)
    }

    const stackLevel = resolveStackLevel(track.services, candidateRange)
    const trackIndex = tracks.indexOf(track)

    const trackService: TrackService = {
      serviceId: item.serviceId,
      name: item.name,
      staff: item.staff,
      startDate: item.startDate,
      endDate: item.endDate,
      startIdx: item.startIdx,
      endIdx: item.endIdx,
      displayRange: item.displayRange,
      status: item.status,
      stackLevel,
    }

    track.services.push(trackService)
    placements.push({
      ...trackService,
      track: trackIndex,
    })
  })

  return placements
}
